import { call, put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { partClassApi, PartClass, PartClassSearchParams } from '../../services/partClassApi';
import {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, setPagination, setStatistics, setBulkOperationLoading, setSuccessMessage,
  FETCH_PART_CLASS, FETCH_PART_CLASS_BY_ID, CREATE_PART_CLASS, UPDATE_PART_CLASS, DELETE_PART_CLASS,
  SEARCH_PART_CLASS, FETCH_PART_CLASS_STATS, BULK_PART_CLASS_ACTIVATE, BULK_PART_CLASS_DEACTIVATE,
  BULK_PART_CLASS_DELETE, EXPORT_PART_CLASS_CSV,
} from '../reducers/partClassSlice';

function* fetchPartClassSaga(action: PayloadAction<PartClassSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const state: RootState = yield select();
    const { pagination, searchParams } = state.partClass;
    const params = { page: pagination.page, pageSize: pagination.pageSize, ...searchParams, ...action.payload };
    const resp = yield call(partClassApi.getAll, params);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: params.page || 1, pageSize: params.pageSize || 10 }));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to fetch Part Class'));
  } finally { yield put(setLoading(false)); }
}

function* fetchPartClassByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    const item = yield call(partClassApi.getById, action.payload);
    yield put(setSelectedItem(item));
  } catch (e: any) {
    yield put(setError(e.message || 'Failed to fetch Part Class item'));
  } finally { yield put(setLoading(false)); }
}

function* createPartClassSaga(action: PayloadAction<Omit<PartClass, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const errors = yield call(partClassApi.validation.validate, action.payload);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const created = yield call(partClassApi.create, action.payload);
    yield put(addItem(created));
    yield put(setSuccessMessage('Part Class created successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to create Part Class')); }
  finally { yield put(setLoading(false)); }
}

function* updatePartClassSaga(action: PayloadAction<{ id: number; item: Partial<PartClass> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const { id, item } = action.payload;
    const errors = yield call(partClassApi.validation.validate, item);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const updated = yield call(partClassApi.update, id, item);
    yield put(updateItem(updated));
    yield put(setSuccessMessage('Part Class updated successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to update Part Class')); }
  finally { yield put(setLoading(false)); }
}

function* deletePartClassSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const resp = yield call(partClassApi.delete, action.payload);
    if (resp.success) { yield put(removeItem(action.payload)); yield put(setSuccessMessage('Part Class deleted successfully')); }
    else { yield put(setError(resp.message || 'Failed to delete Part Class')); }
  } catch (e: any) { yield put(setError(e.message || 'Failed to delete Part Class')); }
  finally { yield put(setLoading(false)); }
}

function* searchPartClassSaga(action: PayloadAction<PartClassSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const resp = yield call(partClassApi.search, action.payload);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: action.payload.page || 1, pageSize: action.payload.pageSize || 10 }));
  } catch (e: any) { yield put(setError(e.message || 'Failed to search Part Class')); }
  finally { yield put(setLoading(false)); }
}

function* fetchStatsSaga(): Generator<any, void, any> {
  try { const stats = yield call(partClassApi.getStatistics); yield put(setStatistics(stats)); } catch (_) {}
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    const resp = yield call(partClassApi.bulkActivate, action.payload);
    if (resp.success) { yield put(setSuccessMessage(resp.message || 'Activated successfully')); yield put({ type: FETCH_PART_CLASS }); }
    else { yield put(setError(resp.message || 'Failed to activate')); }
  } finally { yield put(setBulkOperationLoading(false)); }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    const resp = yield call(partClassApi.bulkDeactivate, action.payload);
    if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deactivated successfully')); yield put({ type: FETCH_PART_CLASS }); }
    else { yield put(setError(resp.message || 'Failed to deactivate')); }
  } finally { yield put(setBulkOperationLoading(false)); }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    const resp = yield call(partClassApi.bulkDelete, action.payload);
    if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deleted successfully')); yield put({ type: FETCH_PART_CLASS }); }
    else { yield put(setError(resp.message || 'Failed to delete')); }
  } finally { yield put(setBulkOperationLoading(false)); }
}

function* exportCsvSaga(action: PayloadAction<PartClassSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const state: RootState = yield select();
    const params = { ...state.partClass.searchParams, ...action.payload };
    const blob = yield call(partClassApi.exportToCSV, params);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `part_class_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
    yield put(setSuccessMessage('Exported successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to export Part Class')); }
  finally { yield put(setLoading(false)); }
}

export function* partClassSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_PART_CLASS, fetchPartClassSaga);
  yield takeEvery(FETCH_PART_CLASS_BY_ID, fetchPartClassByIdSaga);
  yield takeEvery(CREATE_PART_CLASS, createPartClassSaga);
  yield takeEvery(UPDATE_PART_CLASS, updatePartClassSaga);
  yield takeEvery(DELETE_PART_CLASS, deletePartClassSaga);
  yield takeEvery(SEARCH_PART_CLASS, searchPartClassSaga);
  yield takeEvery(FETCH_PART_CLASS_STATS, fetchStatsSaga);
  yield takeEvery(BULK_PART_CLASS_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_PART_CLASS_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_PART_CLASS_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_PART_CLASS_CSV, exportCsvSaga);
} 